import Modal from "../common/Modal";
import { useLanguage } from "contexts/LanguageContext";
import { useEffect, useState } from "react";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { customerToKeyValuePairs } from "utils/customerDataMapper";
import { quotationToKeyValuePairs } from "utils/quotationDataMapper";
import RenderGenerationSteps from "./RenderGenerationSteps";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "lib/firebase";
import toast from "react-hot-toast";
import { DocGenerator } from "utils/docGenerator";
import { useStorage } from "hooks/useStorage";
import { Template } from "types/template";
import { useContracts } from "hooks/useContracts";
import RenderGlobalGenerationSteps from "./RenderGlobalGenerationSteps";
import { useCustomers } from "hooks/useCustomers";
import { useDocumentStorage } from "hooks/useDocumentStorage";
import { useDocumentStorageCategories } from "hooks/useDocumentStorageCategories";

interface DocumentGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentDataProvider: Quotation | Customer | null;
  forceContractTemplates?: boolean;
}

// Type guard functions to check the type of the documentDataProvider
export const isQuotation = (obj: Quotation | Customer): obj is Quotation => {
  return 'items' in obj && 'quotationNumber' in obj;
};

export const isCustomer = (obj: Quotation | Customer): obj is Customer => {
  return 'type' in obj && ('companyName' in obj || 'firstName' in obj);
};

const DocumentGeneratorModal: React.FC<DocumentGeneratorModalProps> = ({ isOpen, onClose, documentDataProvider, forceContractTemplates = false }) => {
  const { t } = useLanguage();
  const [documentData, setDocumentData] = useState<Record<string, string>>({});
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [generatedDocuments, setGeneratedDocuments] = useState<Record<string, Blob[]>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [documentDataProviderCopy, setDocumentDataProviderCopy] = useState<Quotation | Customer | null>(documentDataProvider);
  const [generatedFiles, setGeneratedFiles] = useState<{url: string, fileName: string}[]>([]);
  const [lastGeneratedFile, setLastGeneratedFile] = useState<{url: string, fileName: string} | null>(null);
  const { uploadFile } = useStorage();
  const { addContract } = useContracts();
  const { createDocument } = useDocumentStorage();
  const { categories, createCategory } = useDocumentStorageCategories();
  const [activeStep, setActiveStep] = useState(1);
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filledCount, setFilledCount] = useState(0);
  const [filledPercentage, setFilledPercentage] = useState(0);
  const [sortedDocumentData, setSortedDocumentData] = useState<Record<string, string>>({});
  const { customers } = useCustomers();

  // Generált dokumentumok kategória keresése/létrehozása
  const getOrCreateGeneratedDocumentsCategory = async () => {
    const existingCategory = categories.find(cat => cat.name === 'Generált dokumentumok');
    
    if (existingCategory) {
      return existingCategory.id;
    }

    // Ha nem létezik, hozzuk létre
    try {
      const newCategoryId = await createCategory({
        name: 'Generált dokumentumok',
        description: 'Sablonokból automatikusan generált dokumentumok',
        color: '#8b5cf6', // purple-500
        icon: 'SparklesIcon'
      });
      return newCategoryId;
    } catch (error) {
      console.error('Error creating generated documents category:', error);
      return ''; // Fallback to empty string if creation fails
    }
  };

  useEffect(() => {
    if (isOpen && documentDataProviderCopy) {
      if (isQuotation(documentDataProviderCopy)) {
        const quotationData = quotationToKeyValuePairs(documentDataProviderCopy);
        
        // Debug log to check how paymentDue is being handled
        console.log('Payment due value:', documentDataProviderCopy.paymentDue, 'Mapped data:', quotationData['Fizetési határidő']);
        
        // Ha a quotation-ben nincs elég adat az ügyfélről, akkor keressük meg az ügyfelet
        if (!documentDataProviderCopy.companyName && documentDataProviderCopy.customerId) {
          // Megkeressük az ügyfelet a customers között
          const customer = customers.find(c => c.id === documentDataProviderCopy.customerId);
          if (customer) {
            // Egyesítjük az adatokat, csak ha szükséges
            const customerData = {
              'Cég megnevezése': customer.type === 'organization' ? 
                customer.companyName : 
                `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
              'Cég címe': customer.address ? 
                [
                  customer.address.postalCode,
                  customer.address.city,
                  customer.address.street
                ].filter(Boolean).join(', ') : '',
              'Cég adószáma': customer.taxNumber || ''
            };
            
            // A létező adatokat nem írjuk felül, csak kiegészítjük a hiányzókat
            Object.keys(customerData).forEach(key => {
              if (!quotationData[key] || quotationData[key].trim() === '') {
                quotationData[key] = customerData[key as keyof typeof customerData];
              }
            });
          }
        }
        
        // Biztosítsuk, hogy a fizetési határidő mindig megjelenjen és tartalmazza a "nap" szöveget
        if (documentDataProviderCopy.paymentDue) {
          // Ha már tartalmazza a "nap" szöveget, akkor hagyjuk úgy
          if (!quotationData['Fizetési határidő'] || !quotationData['Fizetési határidő'].includes('nap')) {
            quotationData['Fizetési határidő'] = `${documentDataProviderCopy.paymentDue} nap`;
          }
        }
        
        setDocumentData(quotationData);
      } else if (isCustomer(documentDataProviderCopy)) {
        setDocumentData(customerToKeyValuePairs(documentDataProviderCopy));
      }
    }
  }, [isOpen, documentDataProviderCopy, customers]);

  useEffect(() => {
    if (documentDataProvider) {
      setDocumentDataProviderCopy(documentDataProvider);
    }
  }, [documentDataProvider]);

  // Update document data when template is selected
  useEffect(() => {
    if (selectedTemplate?.type) {
      // Ensure the document type is set based on the template type
      setDocumentData(prev => ({
        ...prev,
        documentType: selectedTemplate.type,
        documentTypeName: selectedTemplate.type === 'contract' 
          ? t('documents.contract') 
          : selectedTemplate.type === 'quotation' 
            ? t('documents.quotation') 
            : t('documents.document')
      }));
    }
  }, [selectedTemplate, t]);

  const handleGenerateDocument = async () => {
    if (!selectedTemplate || selectedFormats.length === 0) return;

    // Ellenőrizzük, hogy van-e kiválasztott sablon és annak van-e érvényes ID-ja
    if (!selectedTemplate || !selectedTemplate.id) {
      toast.error(t("documents.errors.noTemplateSelected"));
      return;
    }

    setIsGenerating(true);
    setGeneratedFiles([]);
    setLastGeneratedFile(null);

    // Ha PDF formátumot választottak, tájékoztassuk a felhasználót a várható időről
    if (selectedFormats.includes('pdf')) {
      toast.loading(t("documents.generatingDocumentWithPdf"), {
        duration: 15000, // Növeljük az időt, hogy biztosan elég legyen
        id: 'pdf-conversion-toast'
      });
    }

    try {
      // Létrehozunk egy adattömböt, amiben egy elem van (a customerData)
      // A DocGenerator várja, hogy tömb legyen, még ha csak egy elemet tartalmaz is
      const dataToUse = [documentData];

      // Használjuk a DocGenerator osztályt a dokumentum generáláshoz
      console.log('Dokumentum generálás kezdése ezekkel a formátumokkal:', selectedFormats);
      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        dataToUse,
        selectedFormats
      );
      console.log('Generált dokumentumok:', generatedDocs);

      // Ellenőrizzük, hogy generált-e minden kért formátumot
      selectedFormats.forEach(format => {
        if (!generatedDocs[format] || generatedDocs[format].length === 0) {
          console.warn(`Nem sikerült ${format} formátumot generálni`);
        } else {
          console.log(`${format} formátum sikeresen generálva, ${generatedDocs[format].length} db`);
        }
      });

      // Beállítjuk a generált dokumentumokat, hogy megjelenjen a UI-on
      setGeneratedDocuments(generatedDocs);

      const newGeneratedFiles: {url: string, fileName: string}[] = [];

      // Mentsük el a generált dokumentumokat a storage-ba
      for (const format of Object.keys(generatedDocs)) {
        for (let i = 0; i < generatedDocs[format].length; i++) {
          const documentBlob = generatedDocs[format][i];
          const fileName = documentDataProviderCopy ? 
            `${isQuotation(documentDataProviderCopy)
              ? documentDataProviderCopy.quotationNumber || documentDataProviderCopy.number || 'quotation'
              : documentDataProviderCopy.id || 'contract'}.${format}`
            : `document_${Date.now()}.${format}`;

          console.log(`${format} feltöltése a storage-ba: ${fileName}`);
          // Feltöltjük a dokumentumot a storage-ba
          const fileUrl = await uploadFile(
            new File(
              [documentBlob],
              fileName,
              {
                type: format === 'docx'
                  ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  : 'application/pdf'
              }
            ),
            documentDataProviderCopy
              ? (isQuotation(documentDataProviderCopy)
                ? `quotations/${documentDataProviderCopy.id}/documents/${fileName}`
                : `customers/${documentDataProviderCopy.id}/documents/${fileName}`)
              : `documents/${fileName}`
          );

          if (fileUrl) {
            console.log(`${format} sikeresen feltöltve: ${fileUrl}`);
            newGeneratedFiles.push({
              url: fileUrl,
              fileName
            });
          }

          // Érvényességi dátumok beállítása
          let validStart = new Date();
          let validEnd = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

          // Ellenőrizzük, hogy a customerData tartalmaz-e szerződési időszakot
          if (documentData['Szerződés kezdete']) {
            try {
              const startDate = new Date(documentData['Szerződés kezdete']);
              if (!isNaN(startDate.getTime())) {
                validStart = startDate;
              }
            } catch (e) {
              console.warn('Hiba a szerződés kezdő dátumának feldolgozása során:', e);
            }
          }

          if (documentData['Szerződés vége']) {
            try {
              const endDate = new Date(documentData['Szerződés vége']);
              if (!isNaN(endDate.getTime())) {
                validEnd = endDate;
              }
            } catch (e) {
              console.warn('Hiba a szerződés záró dátumának feldolgozása során:', e);
            }
          }

          // Elmentjük a dokumentum adatait az adatbázisba
          if (fileUrl && documentDataProviderCopy) {
            // Új dokumentum objektum létrehozása
            const newDocument = {
              id: Date.now().toString(),
              name: fileName,
              originalName: fileName,
              url: fileUrl,
              uploadedAt: new Date(),
              type: selectedTemplate.type || (isQuotation(documentDataProviderCopy) ? "quotation" : "contract"),
              size: documentBlob.size
            };

            // Dokumentum hozzáadása a Dokumentumtárhoz
            try {
              // Generált dokumentumok kategória lekérése/létrehozása
              const categoryId = await getOrCreateGeneratedDocumentsCategory();
              
              const documentStorageData = {
                title: `${selectedTemplate.name} - ${isQuotation(documentDataProviderCopy) 
                  ? (documentDataProviderCopy.quotationNumber || documentDataProviderCopy.number || 'Árajánlat')
                  : (documentDataProviderCopy.companyName || documentDataProviderCopy.firstName + ' ' + documentDataProviderCopy.lastName || 'Ügyfél')}`,
                description: `Generált dokumentum: ${selectedTemplate.name}`,
                categoryId: categoryId,
                customerId: documentDataProviderCopy.id,
                tags: [
                  'generált',
                  selectedTemplate.type || 'dokumentum',
                  format.toUpperCase()
                ],
                isPublic: false,
                creationDate: new Date(),
                file: new File([documentBlob], fileName, {
                  type: format === 'docx'
                    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    : 'application/pdf'
                })
              };

              await createDocument(documentStorageData);
              console.log(`Dokumentum hozzáadva a Dokumentumtárhoz: ${fileName}`);
            } catch (docStorageError) {
              console.error('Hiba a dokumentum Dokumentumtárhoz adása során:', docStorageError);
              // Ne szakítsuk meg a folyamatot, ha a Dokumentumtár mentés sikertelen
            }

            // Ha contract típusú dokumentum, akkor elmentjük az ügyfélhez is
            if (!isQuotation(documentDataProviderCopy)) {
              const customerRef = doc(db, 'customers', documentDataProviderCopy.id);
              await updateDoc(customerRef, {
                documents: documentDataProviderCopy.documents ?
                  [...documentDataProviderCopy.documents, newDocument] :
                  [newDocument],
                updatedAt: serverTimestamp()
              });
            } else {
              // Ha quotation típusú dokumentum, akkor elmentjük a quotation-hoz is
              const quotationRef = doc(db, 'quotations', documentDataProviderCopy.id);
              await updateDoc(quotationRef, {
                documents: documentDataProviderCopy.documents ?
                  [...documentDataProviderCopy.documents, newDocument] :
                  [newDocument],
                updatedAt: serverTimestamp()
              });
            }

            await addContract({
              name: fileName,
              type: selectedTemplate.type || (isQuotation(documentDataProviderCopy) ? "quotation" : "contract"),
              templateId: selectedTemplate.id,
              templateName: selectedTemplate.name,
              fileUrl: fileUrl,
              fileName: fileName,
              status: "draft" as "valid" | "expired" | "draft",
              validStart: validStart,
              validEnd: validEnd,
              customerId: isQuotation(documentDataProviderCopy) ? documentDataProviderCopy.customerId : documentDataProviderCopy.id,
              ...(selectedTemplate.organizationId ? { organizationId: selectedTemplate.organizationId } : {}),
              quotationId: isQuotation(documentDataProviderCopy) ? documentDataProviderCopy.id : null,
            });
          }
        }
      }

      console.log('Generált fájlok:', newGeneratedFiles);
      setGeneratedFiles(newGeneratedFiles);
      
      // Előbb a PDF-et állítsuk be, ha van, különben a DOCX-et
      const pdfFile = newGeneratedFiles.find(file => file.fileName.toLowerCase().endsWith('.pdf'));
      const docxFile = newGeneratedFiles.find(file => file.fileName.toLowerCase().endsWith('.docx'));
      
      if (pdfFile) {
        console.log('PDF fájl beállítása az utolsó generált fájlként:', pdfFile);
        setLastGeneratedFile(pdfFile);
      } else if (docxFile) {
        console.log('DOCX fájl beállítása az utolsó generált fájlként:', docxFile);
        setLastGeneratedFile(docxFile);
      } else if (newGeneratedFiles.length > 0) {
        console.log('Első fájl beállítása az utolsó generált fájlként:', newGeneratedFiles[0]);
        setLastGeneratedFile(newGeneratedFiles[0]);
      }

      // Töröljük a betöltő üzenetet, ha volt
      toast.dismiss('pdf-conversion-toast');
      toast.success(t("documents.generatedDocumentSuccess"));
      
      // Visszaadjuk a PDF fájlt is, ha létezik, máskülönben a DOCX-et
      return pdfFile || docxFile || (newGeneratedFiles.length > 0 ? newGeneratedFiles[0] : null);
    } catch (error) {
      console.error("Hiba a dokumentum generálása közben:", error);
      toast.error(t("documents.generatedDocumentError"));
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNextStep = async () => {
    if (activeStep === 1) {
      setIsLoading(true);
      try {
        const templates = await fetchTemplates();
        setTemplates(templates);
        setActiveStep(2);
      } catch (error) {
        console.error("Hiba a sablonok betöltése közben:", error);
        toast.error(t("documents.errors.templateLoadingError"));
      } finally {
        setIsLoading(false);
      }
    } else if (activeStep === 2) {
      const sortedDocumentData = await sortDocumentData(documentData);
      const filledCount = Object.keys(sortedDocumentData).filter(key => sortedDocumentData[key] !== '').length;
      const filledPercentage = (filledCount / Object.keys(sortedDocumentData).length) * 100;
      setSortedDocumentData(sortedDocumentData);
      setFilledCount(filledCount);
      setFilledPercentage(filledPercentage);
      setActiveStep(3);
    } else if (activeStep === 3) {
      const generatedFile = await handleGenerateDocument();
      if (generatedFile) {
        setGeneratedFiles([generatedFile]);
        setLastGeneratedFile(generatedFile);
      }
    }
  };

  const handleGenerateAndClose = async () => {
    const generatedFile = await handleGenerateDocument();
    if (generatedFile) {
      setGeneratedFiles([generatedFile]);
      setLastGeneratedFile(generatedFile);
    }
    onClose();
  };

  const hasRequiredFieldsFilled = () => {
    return Object.keys(sortedDocumentData).every(key => sortedDocumentData[key] !== '');
  };

  const fetchTemplates = async () => {
    // Implement the logic to fetch templates from your database
    // This is a placeholder and should be replaced with actual implementation
    return [];
  };

  const sortDocumentData = async (data: Record<string, string>) => {
    // Implement the logic to sort document data
    // This is a placeholder and should be replaced with actual implementation
    return data;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('documents.generateDocument')}
        size="full"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 p-4 space-y-6 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-white [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-400/50">
            {documentDataProvider !== null ? (
              <RenderGenerationSteps
                documentDataProvider={documentDataProvider}
                documentData={documentData}
                setDocumentData={setDocumentData}
                isOpen={isOpen}
                onClose={onClose}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                handleGenerateDocument={handleGenerateDocument}
                selectedFormats={selectedFormats}
                setSelectedFormats={setSelectedFormats}
                isGenerating={isGenerating}
                forceContractTemplates={forceContractTemplates}
                generatedFiles={generatedFiles}
              />
            ) : (
              <RenderGlobalGenerationSteps
                documentData={documentData}
                setDocumentData={setDocumentData}
                isOpen={isOpen}
                onClose={onClose}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                handleGenerateDocument={handleGenerateDocument}
                selectedFormats={selectedFormats}
                setSelectedFormats={setSelectedFormats}
                isGenerating={isGenerating}
                setDocumentDataProviderCopy={setDocumentDataProviderCopy}
                generatedFiles={generatedFiles}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DocumentGeneratorModal;
